import { useState, useEffect, useRef } from 'react'
import { api } from '../api'

export default function SystemSettings({ settings, isAdmin, onSaveUser, onSaveDefault, onClose, onPreview, showToast }) {
  const [mode, setMode] = useState('user')  // 'user' | 'default'
  const [form, setForm] = useState({ ...settings })
  const [resetting, setResetting] = useState(false)
  // 임베딩 모델 사용 가능 확인 — { checking, result: {ok, model, dim, latency_ms, error} | null }
  const [embCheck, setEmbCheck] = useState({ checking: false, result: null })

  const runEmbCheck = async (model, location) => {
    setEmbCheck({ checking: true, result: null })
    try {
      const r = await api.modelCheck('embedding', (model || '').trim(), (location || '').trim())
      setEmbCheck({ checking: false, result: r })
    } catch (e) {
      setEmbCheck({ checking: false, result: { ok: false, model, error: e.message } })
    }
  }

  // 임베딩 누락 status / 지금 백필 — { loading, status, running, result }
  const [embBackfill, setEmbBackfill] = useState({ loading: false, status: null, running: false, result: null })

  const loadEmbStatus = async () => {
    setEmbBackfill(s => ({ ...s, loading: true, result: null }))
    try {
      const st = await api.embeddingsStatus()
      setEmbBackfill(s => ({ ...s, loading: false, status: st }))
    } catch (e) {
      setEmbBackfill(s => ({ ...s, loading: false, status: null }))
      showToast?.('error', '임베딩 상태 조회 실패: ' + e.message)
    }
  }

  const runEmbBackfill = async () => {
    setEmbBackfill(s => ({ ...s, running: true, result: null }))
    try {
      const r = await api.embeddingsBackfill()   // {ok, ok_count, failed, elapsed_ms, error?}
      setEmbBackfill(s => ({ ...s, running: false, result: r }))
      // 백필 후 카운트 갱신
      const st = await api.embeddingsStatus()
      setEmbBackfill(s => ({ ...s, status: st }))
    } catch (e) {
      setEmbBackfill(s => ({ ...s, running: false, result: { ok: false, error: e.message } }))
    }
  }

  // 전체 재색인 — 서버의 분산 잡(Firestore 리스) 상태를 폴링해 진행률 표시.
  // state.status: none | running | interrupted | done | failed
  const [reindex, setReindex] = useState({ starting: false, state: null })
  const reindexTimerRef = useRef(null)
  const reindexStateRef = useRef(null)   // 직전 status (running→종료 전환 감지용)

  const fetchReindexStatus = async (poll = false) => {
    if (reindexTimerRef.current) { clearTimeout(reindexTimerRef.current); reindexTimerRef.current = null }
    try {
      const st = await api.embeddingsReindexStatus()
      const was = reindexStateRef.current?.status
      reindexStateRef.current = st
      setReindex(s => ({ ...s, state: st }))
      if (poll && st?.status === 'running') {
        reindexTimerRef.current = setTimeout(() => fetchReindexStatus(true), 4000)
      } else if (poll && was === 'running' && st?.status !== 'running') {
        loadEmbStatus()   // 재색인 종료 → 누락 카운트 갱신
      }
    } catch { /* admin 아님/일시 오류 — 표시만 생략 */ }
  }

  // 공유 기본값 모드 진입 시 1회 조회 — 진행 중이면 폴링 시작 (새로고침/재접속 복원)
  useEffect(() => {
    if (mode !== 'default') return
    fetchReindexStatus(true)
    return () => {
      if (reindexTimerRef.current) { clearTimeout(reindexTimerRef.current); reindexTimerRef.current = null }
    }
  }, [mode])   // eslint-disable-line react-hooks/exhaustive-deps

  const startReindex = async () => {
    const n = embBackfill.status?.total
    if (!window.confirm(
      `모든 SV 임베딩${n != null ? ` (문서 ${n}개)` : ''}을 현재 설정 모델로 다시 계산합니다.\n` +
      '문서 수에 따라 수 분 이상 걸릴 수 있고 임베딩 호출 비용이 발생합니다.\n계속할까요?')) return
    setReindex(s => ({ ...s, starting: true }))
    try {
      const r = await api.embeddingsReindex()   // {ok, state?, error?}
      if (!r.ok) showToast?.('error', r.error || '재색인 시작 실패')
      if (r.state) { reindexStateRef.current = r.state; setReindex(s => ({ ...s, state: r.state })) }
      fetchReindexStatus(true)
    } catch (e) {
      showToast?.('error', '재색인 시작 실패: ' + e.message)
    } finally {
      setReindex(s => ({ ...s, starting: false }))
    }
  }

  // 마운트 시점의 원본 테마/폰트 — 저장 없이 닫으면 복원
  const originalRef = useRef({ theme: settings.theme, font_size: settings.font_size })
  const savedRef = useRef(false)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  // 드롭다운/입력 변경 즉시 라이브 프리뷰
  useEffect(() => {
    onPreview?.({ theme: form.theme, font_size: form.font_size })
  }, [form.theme, form.font_size, onPreview])

  // 언마운트 시 저장 안 했으면 원본 복원
  useEffect(() => {
    return () => {
      if (!savedRef.current) onPreview?.(originalRef.current)
    }
  }, [onPreview])

  const handleSave = () => {
    savedRef.current = true
    if (mode === 'default') {
      onSaveDefault({ ...form })
    } else {
      onSaveUser({ ...form })
    }
  }

  const switchMode = async (next) => {
    if (next === mode) return
    if (next === 'default') {
      try {
        const { settings: def, exists } = await api.getDefaultSettings()
        if (exists && def && Object.keys(def).length > 0) {
          setForm(f => ({ ...f, ...def }))
        }
        setMode('default')
      } catch (e) {
        showToast?.('error', '공유 기본값 로드 실패: ' + e.message)
      }
    } else {
      setForm({ ...settings })
      setMode('user')
    }
  }

  const handleResetDefaults = async () => {
    setResetting(true)
    try {
      const defaults = await api.getDefaults()
      setForm(prev => ({
        ...prev,
        font_size: defaults.font_size ?? prev.font_size,
        theme: defaults.theme ?? prev.theme,
        search_grounding: defaults.search_grounding ?? prev.search_grounding,
      }))
    } catch (e) {
      showToast?.('error', '기본값 로드 실패: ' + e.message)
    } finally {
      setResetting(false)
    }
  }

  return (
    <div className="settings-page">
      <div className="settings-page-header">
        <span className="settings-page-title">
          시스템 설정
          {/* KBNL-129 후속: 모드 뱃지 제거 (사용자 결정) — 토글 active 가 모드 표시 */}
        </span>
        {isAdmin && (
          <div className="settings-mode-toggle">
            <button
              className={`mode-btn ${mode === 'user' ? 'active' : ''}`}
              onClick={() => switchMode('user')}
            >내 설정</button>
            <button
              className={`mode-btn ${mode === 'default' ? 'active' : ''}`}
              onClick={() => switchMode('default')}
            >공유 기본값</button>
          </div>
        )}
        <button className="btn-icon" onClick={onClose} title="닫기">✕</button>
      </div>

      <div className="settings-page-body">
        <div className="settings-body">
          {/* ── 에디터 / UI ─────────────────────────────────────────────── */}
          <section className="settings-section">
            <div className="section-head">
              <span className="section-title">에디터 / UI</span>
            </div>
            <div className="section-body">
              <div className="form-row">
                <div className="form-group form-group-half">
                  <label>코드 폰트 크기</label>
                  <div className="input-with-unit">
                    <input
                      className="field"
                      type="number"
                      min="10"
                      max="24"
                      value={form.font_size || 13}
                      onChange={e => set('font_size', Number(e.target.value))}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>
                <div className="form-group form-group-half">
                  <label>테마</label>
                  <select className="field" value={form.theme || 'light'} onChange={e => set('theme', e.target.value)}>
                    <option value="light">라이트 (KB)</option>
                    <option value="dark">다크</option>
                  </select>
                </div>
              </div>
              <div className="form-hint">폰트 크기와 테마는 코드 에디터에 적용됩니다.</div>
            </div>
          </section>

          {/* ── Sandbox ────────────────────────────────────────────────── */}
          <section className="settings-section">
            <div className="section-head">
              <span className="section-title">Sandbox</span>
            </div>
            <div className="section-body">
              {/* KBNL-123: 샌드박스 대화 보존 기간(TTL) — 서버 purge 는 '공유 기본값'
                  설정만 읽으므로 관리자 default 모드에서만 노출. 0/빈 값 = 무기한. */}
              {mode === 'default' && (
                <div className="form-group">
                  <label>대화 보존 기간 (일)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="field"
                    value={form.sandbox_session_ttl_days ?? ''}
                    onChange={e => set('sandbox_session_ttl_days', e.target.value === '' ? '' : Number(e.target.value))}
                    placeholder="0 = 무기한 (기본)"
                  />
                  <div className="form-hint">
                    마지막 사용 후 이 일수가 지나면 샌드박스 세션(대화+메시지)이 <strong>영구 삭제</strong>됩니다.
                    <strong> 0 또는 빈 값 = 무기한(삭제 안 함, 기본값)</strong>.
                    저장 후 재배포 없이 최대 30분 내 정리 주기에 반영됩니다.
                  </div>
                </div>
              )}

              {/* Google Search Grounding */}
              <label className="checkbox-card">
                <input
                  type="checkbox"
                  checked={form.search_grounding !== false}
                  onChange={e => set('search_grounding', e.target.checked)}
                />
                <div className="checkbox-card-text">
                  <div className="checkbox-card-title">Google Search Grounding 사용</div>
                  <div className="checkbox-card-hint">
                    Gemini 응답이 Google 검색 결과로 근거(grounding) 부여됩니다.
                    최신 정보가 필요한 질문에 정확도가 높아지지만 응답 시간이 약간 늘어날 수 있습니다.
                    기본값: 활성. Sandbox 화면 헤더에서도 ON/OFF 가능.
                  </div>
                </div>
              </label>

              {/* KBNL-70: 메타 주입 토글 — 메타 설정에서 선택한 항목 자체는 보존하되
                  Sandbox prompt 에 주입할지만 ON/OFF. Sandbox 헤더에서도 토글 가능. */}
              <label className="checkbox-card">
                <input
                  type="checkbox"
                  checked={form.sandbox_inject_table_schema !== false}
                  onChange={e => set('sandbox_inject_table_schema', e.target.checked)}
                />
                <div className="checkbox-card-text">
                  <div className="checkbox-card-title">테이블 스키마 주입</div>
                  <div className="checkbox-card-hint">
                    메타 설정에서 선택한 BigQuery 테이블 스키마를 LLM system prompt 에 포함합니다.
                    OFF 면 선택은 그대로 두고 주입만 스킵합니다.
                    기본값: 활성. Sandbox 화면 헤더에서도 ON/OFF 가능.
                  </div>
                </div>
              </label>

              <label className="checkbox-card">
                <input
                  type="checkbox"
                  checked={form.sandbox_inject_glossary !== false}
                  onChange={e => set('sandbox_inject_glossary', e.target.checked)}
                />
                <div className="checkbox-card-text">
                  <div className="checkbox-card-title">용어집(Glossary) 주입</div>
                  <div className="checkbox-card-hint">
                    메타 설정에서 선택한 Dataplex 용어집을 LLM system prompt 에 포함합니다.
                    OFF 면 선택은 그대로 두고 주입만 스킵합니다.
                    기본값: 활성. Sandbox 화면 헤더에서도 ON/OFF 가능.
                  </div>
                </div>
              </label>
            </div>
          </section>

          {/* ── 유사 SV 검색 (임베딩) — admin 공유 기본값 전용 ──────────────
               임베딩은 공유 검색 인프라(모델이 섞이면 벡터 비교 불가)라 공유 기본값
               으로만 관리. 서버가 '시스템 설정 → env → 기본값' 순으로 읽음 (TTL 캐시
               ~60s, 저장 시 즉시 무효화). 조직 정책으로 모델이 막힌 환경 대응. */}
          {mode === 'default' && (
            <section className="settings-section">
              <div className="section-head">
                <span className="section-title">유사 SV 검색 (임베딩)</span>
              </div>
              <div className="section-body">
                <label className="checkbox-card">
                  <input
                    type="checkbox"
                    checked={form.embedding_enabled !== false}
                    onChange={e => set('embedding_enabled', e.target.checked)}
                  />
                  <div className="checkbox-card-text">
                    <div className="checkbox-card-title">임베딩(vector) 검색 사용</div>
                    <div className="checkbox-card-hint">
                      SV 저장 시 의미 임베딩을 생성하고, 유사 SV 찾기에서 vector 검색을 사용합니다.
                      임베딩 모델이 조직 정책 등으로 차단된 환경에서는 OFF — 테이블/식별자 겹침
                      채널만으로 동작합니다. <strong>기본값: 사용함</strong>.
                    </div>
                  </div>
                </label>

                <div className="form-group">
                  <label>임베딩 모델 / 리전</label>
                  {/* 리전별 모델 가용성이 다르므로(예: 최신 모델은 global 에만) 모델과
                      리전을 한 쌍으로 지정. 기본: 서울(asia-northeast3) + 기본 모델. */}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                      className="field"
                      style={{ flex: 2, minWidth: 220 }}
                      value={form.embedding_model ?? ''}
                      onChange={e => set('embedding_model', e.target.value)}
                      placeholder="text-multilingual-embedding-002 (기본)"
                      disabled={form.embedding_enabled === false}
                    />
                    <input
                      className="field"
                      style={{ flex: 1, minWidth: 130 }}
                      value={form.embedding_location ?? ''}
                      onChange={e => set('embedding_location', e.target.value)}
                      placeholder="asia-northeast3 (기본)"
                      title="임베딩 호출 리전 — 모델이 없는 리전이면 404. 최신 모델은 'global'"
                      disabled={form.embedding_enabled === false}
                    />
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ whiteSpace: 'nowrap' }}
                      disabled={embCheck.checking || form.embedding_enabled === false}
                      onClick={() => runEmbCheck(form.embedding_model, form.embedding_location)}
                      title="입력한 모델/리전 조합이 실제 호출 가능한지 1회 테스트"
                    >{embCheck.checking ? '확인 중…' : '✓ 사용 가능 확인'}</button>
                  </div>
                  {embCheck.result && (
                    <div className="form-hint" style={{ marginTop: 6 }}>
                      {embCheck.result.ok ? (
                        <span style={{ color: '#15803d' }}>
                          ✓ 사용 가능 — 모델 <code>{embCheck.result.model}</code>,
                          차원 {embCheck.result.dim}, {embCheck.result.latency_ms}ms
                        </span>
                      ) : (
                        <span style={{ color: '#b91c1c' }}>
                          ✗ 사용 불가 — {embCheck.result.error}
                        </span>
                      )}
                      {embCheck.result.ok && embCheck.result.dim !== 768 && (
                        <div style={{ color: '#b45309', marginTop: 4 }}>
                          ⚠ 이 모델의 벡터 차원({embCheck.result.dim})이 기존 인덱스/임베딩(768)과
                          다릅니다. 모델 변경 시 <strong>Firestore vector index 를 해당 차원으로
                          재생성</strong>하고 <code>scripts/reindex_sv_embeddings.py</code> 로
                          <strong> 전체 재색인</strong>해야 vector 검색이 동작합니다.
                        </div>
                      )}
                    </div>
                  )}
                  <div className="form-hint">
                    모델을 바꾼 뒤에는 기존 SV 임베딩과 벡터가 호환되지 않으므로
                    아래 <strong>[전체 재색인 실행]</strong> 버튼(또는
                    <code> scripts/reindex_sv_embeddings.py</code>)으로 전체 재색인이 필요합니다.
                  </div>
                </div>

                {/* 사용 불가 시 키워드 매치 자동 전환 (기본 ON) — 실패 캐시(cooldown) */}
                <label className="checkbox-card">
                  <input
                    type="checkbox"
                    checked={form.embedding_auto_fallback !== false}
                    onChange={e => set('embedding_auto_fallback', e.target.checked)}
                    disabled={form.embedding_enabled === false}
                  />
                  <div className="checkbox-card-text">
                    <div className="checkbox-card-title">사용 불가 시 키워드 매치로 자동 전환</div>
                    <div className="checkbox-card-hint">
                      임베딩 호출이 실패하면(조직 정책 차단 등) 일정 시간(기본 5분) 임베딩을
                      건너뛰고 <strong>키워드/식별자 매치만으로 즉시 검색</strong>합니다 — 검색·저장이
                      느려지지 않습니다. 시간이 지나면 자동 재시도해 복구 시 원래대로 돌아옵니다.
                      <strong> 기본값: 사용함</strong>.
                    </div>
                  </div>
                </label>

                {/* 임베딩 누락 SV 카운트 + 지금 백필 — 사용 불가 중 저장된 SV 치유.
                    30분 자동 백필 루프도 돌지만, admin 이 즉시 처리할 수 있게 버튼 제공. */}
                <div className="form-group">
                  <label>임베딩 색인 상태</label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={embBackfill.loading}
                      onClick={loadEmbStatus}
                    >{embBackfill.loading ? '조회 중…' : '누락 확인'}</button>
                    {embBackfill.status && (
                      <span style={{ fontSize: 12.5 }}>
                        누락(미색인/모델 불일치) <strong>{embBackfill.status.missing}</strong> /
                        전체 {embBackfill.status.total}
                        {embBackfill.status.unavailable_reason && (
                          <span style={{ color: '#b45309' }}> · 현재 임베딩 사용 불가: {embBackfill.status.unavailable_reason.slice(0, 80)}</span>
                        )}
                      </span>
                    )}
                    {embBackfill.status?.missing > 0 && !embBackfill.status?.unavailable_reason && (
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={embBackfill.running}
                        onClick={runEmbBackfill}
                        title="누락된 SV 임베딩을 지금 생성 (30분 주기 자동 백필과 별개로 즉시 실행)"
                      >{embBackfill.running ? '백필 중…' : '지금 백필'}</button>
                    )}
                  </div>
                  {embBackfill.result && (
                    <div className="form-hint" style={{ marginTop: 6 }}>
                      {embBackfill.result.ok
                        ? <span style={{ color: '#15803d' }}>✓ 백필 완료 — 성공 {embBackfill.result.ok_count} / 실패 {embBackfill.result.failed} ({embBackfill.result.elapsed_ms}ms)</span>
                        : <span style={{ color: '#b91c1c' }}>✗ 백필 실패 — {embBackfill.result.error}</span>}
                    </div>
                  )}
                  <div className="form-hint">
                    임베딩 사용 불가 상태에서 저장된 SV 는 색인이 없습니다. 복구되면
                    30분 주기 자동 백필이 치유하며, 이 버튼으로 즉시 실행할 수도 있습니다.
                  </div>
                </div>

                {/* 전체 재색인 — 모델 교체 후 전량 강제 재계산. Firestore 분산 리스
                    (하트비트 90s stale 판정)로 여러 인스턴스에서 눌러도 한 번만 실행.
                    진행률은 잡 doc 폴링(4s) — 새로고침해도 진행 표시 복원. */}
                <div className="form-group">
                  <label>전체 재색인</label>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn-secondary"
                      disabled={reindex.starting || reindex.state?.status === 'running' || form.embedding_enabled === false}
                      onClick={startReindex}
                      title="모든 SV 임베딩을 현재 모델로 강제 재계산 (모델 변경 후 필수)"
                    >{reindex.state?.status === 'running' ? '재색인 진행 중…'
                      : (reindex.starting ? '시작 중…' : '전체 재색인 실행')}</button>
                    {reindex.state?.status === 'running' && (
                      <span style={{ fontSize: 12.5, display: 'inline-flex', alignItems: 'center' }}>
                        <span className="cv-inline-spinner" />
                        {reindex.state.processed ?? 0}/{reindex.state.total ?? '?'} 처리 중
                        {reindex.state.started_by_name && <>&nbsp;· 시작: {reindex.state.started_by_name}</>}
                        {reindex.state.started_at && <>&nbsp;({new Date(reindex.state.started_at * 1000).toLocaleTimeString()})</>}
                      </span>
                    )}
                  </div>
                  {reindex.state?.status === 'done' && (
                    <div className="form-hint" style={{ marginTop: 6 }}>
                      <span style={{ color: '#15803d' }}>
                        ✓ 재색인 완료 — 성공 {reindex.state.ok ?? 0} / 실패 {reindex.state.failed ?? 0}
                        {reindex.state.cleared > 0 && <> / 제거 {reindex.state.cleared}</>}
                        {reindex.state.dims?.length > 0 && <> · 벡터 차원 {reindex.state.dims.join(', ')}</>}
                        {reindex.state.finished_at && <> ({new Date(reindex.state.finished_at * 1000).toLocaleString()})</>}
                      </span>
                      {reindex.state.dims?.length > 0 && !reindex.state.dims.includes(768) && (
                        <div style={{ color: '#b45309', marginTop: 4 }}>
                          ⚠ 벡터 차원이 기존 인덱스(768)와 다릅니다 — Firestore vector index 를
                          해당 차원으로 재생성해야 vector 검색이 동작합니다
                          (<code>scripts/setup_firestore_vector_index.md</code> 참조).
                        </div>
                      )}
                    </div>
                  )}
                  {reindex.state?.status === 'failed' && (
                    <div className="form-hint" style={{ marginTop: 6, color: '#b91c1c' }}>
                      ✗ 재색인 실패 — {reindex.state.error}
                    </div>
                  )}
                  {reindex.state?.status === 'interrupted' && (
                    <div className="form-hint" style={{ marginTop: 6, color: '#b45309' }}>
                      ⚠ 이전 재색인이 중단된 것으로 보입니다 (서버 재시작 등으로 하트비트 끊김).
                      다시 실행할 수 있습니다.
                    </div>
                  )}
                  <div className="form-hint">
                    모든 SV 임베딩을 현재 모델로 <strong>강제 재계산</strong>합니다 (모델 변경 후 필수).
                    여러 서버 인스턴스에서 동시에 눌러도 잠금으로 <strong>한 번만</strong> 실행됩니다.
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      <div className="settings-page-footer">
        <button className="btn-secondary" onClick={handleResetDefaults} disabled={resetting}>
          {resetting ? '로드 중...' : '서버 기본값 초기화'}
        </button>
        <div style={{ flex: 1 }} />
        <button className="btn-secondary" onClick={onClose}>취소</button>
        <button className="btn-primary" onClick={handleSave}>저장</button>
      </div>
    </div>
  )
}
